import { environment } from '@env';

/**
 * Developer-facing feature-flag overrides.
 *
 * Lets a developer force any PostHog feature flag on/off from the browser
 * console without touching the PostHog dashboard — handy for testing a
 * dark-shipped feature on staging. Overrides are honoured on every build
 * EXCEPT real production (`envName === 'production'`), so they can never leak
 * a flag to live users.
 *
 * Usage (console):
 *   featureFlags.on('cash-register');   location.reload();
 *   featureFlags.off('cash-register');  location.reload();
 *   featureFlags.clear();               location.reload();   // all
 *   featureFlags.list();
 *
 * Flags are read at component-init time, so a reload is required to apply.
 */

const STORAGE_KEY = 'tvd.ff.overrides';

/** Overrides apply on local/dev/staging, never on real production. */
const overridesEnabled = environment.envName !== 'production';

type Overrides = Record<string, boolean>;

function read(): Overrides {
  if (!overridesEnabled) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    // Guard against a hand-edited value (e.g. `null`, an array, a string) so
    // downstream `hasOwnProperty`/spread can't throw into a computed().
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Overrides)
      : {};
  } catch {
    return {};
  }
}

function write(next: Overrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // private mode / quota — overrides are a dev convenience, ignore.
  }
}

/**
 * Returns the forced value for `feature` if a dev override is set, otherwise
 * `undefined` so the caller falls back to PostHog. Always `undefined` on
 * production.
 */
export function readFlagOverride(feature: string): boolean | undefined {
  const overrides = read();
  return Object.prototype.hasOwnProperty.call(overrides, feature)
    ? overrides[feature]
    : undefined;
}

/**
 * Installs `window.featureFlags` so flags can be toggled from the console.
 * No-op on production.
 */
export function installFeatureFlagConsole(): void {
  if (!overridesEnabled) return;

  const api = {
    on(feature: string): string {
      write({ ...read(), [feature]: true });
      return `Override set: ${feature}=true. Reload to apply.`;
    },
    off(feature: string): string {
      write({ ...read(), [feature]: false });
      return `Override set: ${feature}=false. Reload to apply.`;
    },
    clear(feature?: string): string {
      if (feature === undefined) {
        write({});
        return 'All flag overrides cleared. Reload to apply.';
      }
      const next = read();
      delete next[feature];
      write(next);
      return `Override cleared: ${feature}. Reload to apply.`;
    },
    list(): Overrides {
      return read();
    },
  };

  (globalThis as unknown as { featureFlags?: typeof api }).featureFlags = api;
}
